import { getWeekPayFormTotals } from "./erp-week-pay-form-totals";
import { action, computed, observable } from "mobx";
import { isFinite, sumBy, toFinite, toNumber, toString, trim } from "lodash";
import {
  AccommodationConfirmationBookingInfoFragment,
  AddAccommodationDocs,
  BookingHospitalityGet,
  BookingScheduleSearch_Get,
  ConfirmAccommodationInput,
  UpdateAccommodationConfirmation,
  UploadBlob,
  CityInhabitants,
  AccommodationConfirmation_GetCityInhabitants
} from "@graphql";
import { FormErrors, FormStore } from "@modules/formx";
import { Accommodation } from "@containers/VirtualSelector/AccommodatioSelector";
import {
  dateToWeekYear,
  durationsToWeeks,
  weeksToDurations,
  WeekYear,
  weekYearToDate
} from "@bonliva-crm/common";
import { AccommodationConfirmationFormData, validationSchema } from "./utils";
import { globalStore } from "@store";
import moment from "moment";
import { LegalPersonType } from "@bonliva-crm/shared";
import locale from "@bonliva-crm/locale";

export class AccommodationConfirmationStore {
  form: FormStore<AccommodationConfirmationFormData>;
  @observable.ref accommodationData: Accommodation | null = null;
  @observable selectedOfferId = "";
  @observable missingTaxError: string = null;
  @observable.ref public cityInhabitants: CityInhabitants[] = [];
  @observable public cityInhabitantsLoading = false;
  @observable public cityInhabitantsError: string | null = null;

  get isNew() {
    return !this.confirmationId;
  }

  /**
   * ERP-owned rows are a read-only mirror of an ERP taxation — the whole form
   * is locked and the user is pointed back to ERP. Legacy CRM rows stay
   * editable. Driven off `source` carried on the form values.
   */
  @computed get readOnly() {
    return this.form.values.source === "erp";
  }

  /** ERP taxation id — present only on "erp"-sourced rows; powers the deep-link. */
  @computed get erpTaxationId() {
    return this.form.values.taxationId ?? null;
  }

  constructor(
    public confirmationId: string | null,
    public bookingId: string,
    public bookingData: AccommodationConfirmationBookingInfoFragment,
    initialValues: AccommodationConfirmationFormData,
    disabledWeeks: Date[]
  ) {
    const isErpMirror = initialValues.source === "erp";

    this.form = new FormStore({
      initialValues,
      // ERP-owned mirrors load fully disabled — every formx input
      // (Text/Select/Date/Checkbox) honours the form-level disabled flag.
      // No schema/validate either: ERP doesn't send accommodation/city/area, so
      // validating would flag fields the user can never fix on a read-only form.
      schema: isErpMirror ? undefined : validationSchema,
      initialDisabled: isErpMirror,
      validate: isErpMirror
        ? undefined
        : ({ values }) => {
            const errors: FormErrors<AccommodationConfirmationFormData> = {};

            if (
              this.bookingData?.BookingGroup.hospital?.Address?.Country === "Sweden" &&
              !values.city
            ) {
              errors.city = "City";
            }

            if (
              this.bookingData?.Consultant?.LegalPersonType ===
                LegalPersonType.Subcontractor &&
              !values.totalExVAT
            ) {
              errors.totalExVAT = locale.TotalExcludeVAT;
            }

            const actualTotal = sumBy(
              values.weeks.filter((x) => x.isTaxed),
              (x) => toFinite(x.cost)
            );

            if (actualTotal?.toFixed(2) !== toFinite(values.totalCost)?.toFixed(2)) {
              errors.totalCost =
                "Totala verkliga kostnaden matchar inte fördelade beloppet över veckorna";
            }

            values.weeks.forEach((x, i) => {
              if (!x.isTaxed) {
                return;
              }

              if (toFinite(x.cost) < 0) {
                errors[`weeks.${i}.cost`] = "Var god fyll i kostnad";
              }

              if (toFinite(x.extraCost) < 0) {
                errors[`weeks.${i}.extraCost`] = "Var god fyll i kostnad";
              }

              if (toFinite(x.nights) <= 0) {
                errors[`weeks.${i}.nights`] = "Var god fyll i antal nätter";
              }
            });

            if (this.isNew && values.files.length === 0) {
              errors.files = "Add documents";
            }

            if (this.isConsultantEmployee && !values.squareFootageAttached) {
              errors.squareFootageAttached = locale.squareFootageAttached;
            }

            if (!isValidNumber(values.totalCost) || toNumber(values.totalCost) < 0) {
              errors.totalCost = "Var god fyll i total kostnad";
            }

            if (this.isConsultantEmployee && !values.area) {
              errors.area = "Area (skriv 0 på underleverantör)";
            }

            return errors;
          },
      onSubmit: () => this.submit(),
      onSetValue: (options) => {
        const taxedWeeks = options.store.values.weeks.filter((x) => x.isTaxed);
        if (options.name === "totalCost" && taxedWeeks.length === 1) {
          taxedWeeks[0].cost = toString(options.value);
        }

        if (
          options.name.match(/weeks.\d+.startDate/gm) ||
          options.name.match(/weeks.\d+.endDate/gm)
        ) {
          const weekNumber = options.name.split(".")[1];
          const week = options.store.values.weeks[weekNumber];

          if (week?.startDate && week?.endDate) {
            week.nights = Math.abs(
              moment(week.startDate)
                .startOf("day")
                .diff(moment(week.endDate).startOf("day"), "days", false)
            );
          }
        }

        this.recalcTax();
      }
    });

    (window as any).accommodationDocumentUploaderStore = this;

    this.loadCityInhabitants();
  }

  @action private loadCityInhabitants = async () => {
    this.cityInhabitantsLoading = true;

    try {
      const res = await AccommodationConfirmation_GetCityInhabitants.func(
        {},
        { fetchPolicy: "network-only" }
      );
      this.cityInhabitants = res.data.config.cityInhabitants ?? [];
    } catch (e) {
      console.error(e);
      this.cityInhabitantsError = locale["Failed to load city inhabitants"];
      this.cityInhabitants = [];
    } finally {
      this.cityInhabitantsLoading = false;
    }
  };

  @computed get totalNights() {
    return sumBy(this.form.values.weeks, (x) => toFinite(x.nights));
  }

  @computed get totalTax() {
    return sumBy(this.form.values.weeks, (x) => getWeekPayFormTotals(x).tax);
  }

  @computed get totalExtraCost() {
    return sumBy(this.form.values.weeks, (x) => toFinite(x.extraCost));
  }

  @computed get disabledWeeks(): Date[] {
    return this.form.values.weeks
      .filter((x) => this.isWeekDisabled(x.weekYear))
      .map((x) => weekYearToDate(x.weekYear));
  }

  isWeekDisabled(weekYear: WeekYear) {
    const weekExt = this.bookingData.BookingGroup.bookings.find((x) => {
      const w = dateToWeekYear(x.Week);

      return w.Week === weekYear.Week && w.Year === weekYear.Year;
    });

    return weekExt ? weekExt.IsConsultantPayed : false;
  }

  @action submit = async () => {
    const { values } = this.form;
    this.form.setSubmitting(true);

    try {
      const files = [];

      for (const file of this.form.values.files) {
        await UploadBlob.func({ file }).then((x) => files.push(x.data.uploadBlob));
      }

      const input: ConfirmAccommodationInput = {
        accommodationId: values.accommodationId,
        bookingId: this.bookingId,
        offerId: values.offerId,
        files,
        weeks: values.weeks.map((x) => ({
          weekYear: x.weekYear,
          tax: x.tax,
          extraCost: toFinite(x.extraCost),
          extraDescription: x.extraDescription,
          startDate: x.startDate,
          endDate: x.endDate,
          cost: toFinite(x.cost),
          isTaxed: x.isTaxed,
          nights: toFinite(x.nights),
          note: x.note
        })),
        area: toFinite(values.area),
        totalCost: toFinite(values.totalCost),
        totalExVAT: values.totalExVAT,
        city: values.city,
        squareFootageAttached: values.squareFootageAttached
      };

      const promise = this.isNew
        ? AddAccommodationDocs.func(
            { input },
            {
              refetchQueries: [
                BookingHospitalityGet.OperationName,
                BookingScheduleSearch_Get.OperationName
              ]
            }
          )
        : UpdateAccommodationConfirmation.func(
            { id: this.confirmationId, input },
            {
              refetchQueries: [
                BookingHospitalityGet.OperationName,
                BookingScheduleSearch_Get.OperationName
              ]
            }
          );

      await promise;
      globalStore().localPubSub.accommodationConfirmationUpdated.trigger();
    } catch (e) {
      console.error(e);
      globalStore().snackbars.error(`Cannot save form`);
    } finally {
      this.form.setSubmitting(false);
    }
  };

  @action setWeeks = (value: CustomDuration[]) => {
    const currentWeekYears = this.form.values.weeks.map((x) =>
      weekYearToString(x.weekYear)
    );

    const weekYears = durationsToWeeks(value, true)
      .map(dateToWeekYear)
      .map(weekYearToString);

    const newWeeks = weekYears.filter((x) => !currentWeekYears.includes(x));
    const removeWeeks = weekYears.filter((x) => currentWeekYears.includes(x));

    this.form.values.weeks = this.form.values.weeks.filter((x) =>
      removeWeeks.includes(weekYearToString(x.weekYear))
    );

    this.form.values.weeks.push(
      ...newWeeks.map((x) => {
        const parsed = parseWeekYear(x);

        return {
          weekYear: { Week: parsed.week, Year: parsed.year },
          cost: "0",
          extraCost: "0",
          extraDescription: null,
          startDate: null,
          endDate: null,
          tax: 0,
          nights: "0",
          isTaxed: false,
          note: null
        };
      })
    );

    this.form.validate();
    this.form.changed();
  };

  @action setTaxPaymentWeek = (value: CustomDuration[]) => {
    const currentTaxWeekYears = this.form.values.weeks
      .filter((x) => x.isTaxed)
      .map((x) => weekYearToString(x.weekYear));

    const weekYears = durationsToWeeks(value, true)
      .map(dateToWeekYear)
      .map(weekYearToString);

    const newWeeks = weekYears.filter((x) => !currentTaxWeekYears.includes(x));
    const removeWeeks = currentTaxWeekYears.filter((x) => !weekYears.includes(x));

    newWeeks.forEach((w) => {
      const parsed = parseWeekYear(w);
      const found = this.form.values.weeks.find(
        (x) => weekYearToString(x.weekYear) === w
      );

      if (found) {
        found.isTaxed = true;
        found.cost = "0";
        found.nights = "1";
      } else {
        this.form.values.weeks.push({
          weekYear: {
            Week: parsed.week,
            Year: parsed.year
          },
          tax: 0,
          isTaxed: true,
          cost: "0",
          extraCost: "0",
          extraDescription: null,
          startDate: null,
          endDate: null,
          nights: "1",
          note: null
        });
      }
    });

    removeWeeks.forEach((w) => {
      const found = this.form.values.weeks.find(
        (x) => weekYearToString(x.weekYear) === w
      );

      if (found) {
        found.isTaxed = false;
        found.cost = "0";
        found.extraCost = "0";
        found.startDate = null;
        found.endDate = null;
        found.nights = "0";
        found.tax = 0;
      }
    });

    this.form.validate();
    this.form.changed();
  };

  @action setAccommodation = (data: Accommodation | null) => {
    if (this.accommodationData?._id === data?._id) {
      return;
    }

    this.accommodationData = data;
    this.form.values.accommodationId = data?._id ?? null;
    this.form.values.area = data?.Offers[0]?.Area.toString() ?? "";
    this.form.values.offerId = data.Offers.length === 1 ? data.Offers[0]._id : "";

    if (this.cityInhabitants.find((x) => x.city === data.Address?.City)) {
      this.form.values.city = data.Address.City;
    }

    this.form.validate();
    this.form.changed();
  };

  @action clearAccommodation = () => {
    this.form.values.accommodationId = "";
    this.accommodationData = null;
    this.form.values.offerId = "";
    this.form.validate();
  };

  @action distributeCosts = () => {
    const totalNights = sumBy(
      this.form.values.weeks.filter((x) => !this.isWeekDisabled(x.weekYear)),
      (x) => toFinite(x.nights)
    );

    const taxedWeeks = this.form.values.weeks
      .filter((x) => x.isTaxed)
      .sortBy((x) => x.weekYear.Year * 52 + x.weekYear.Week)
      .filter((x) => !this.isWeekDisabled(x.weekYear));

    const disabledCostSum = sumBy(
      this.form.values.weeks.filter((x) => this.isWeekDisabled(x.weekYear)),
      (x) => toFinite(x.cost)
    );

    const total = toFinite(this.form.values.totalCost) - disabledCostSum;

    if (!taxedWeeks.length || !total) {
      return;
    }

    // remove disabled

    taxedWeeks.forEach((x) => {
      x.cost = "0";
    });

    taxedWeeks.forEach((x, i) => {
      if (total > 0) {
        if (i < taxedWeeks.length - 1) {
          const cost = (total / totalNights) * toFinite(x.nights);
          x.cost = cost.toFixed(2);
        } else {
          const remaining = total - sumBy(taxedWeeks, (x) => toFinite(x.cost));
          x.cost = remaining.toFixed(2);
        }
      }
    });

    this.form.validate();
    this.form.changed();
  };

  @action recalcTax = () => {
    this.form.values.weeks.forEach((w) => {
      const city = this.cityInhabitants.find((x) => x.city === this.form.values.city);
      const factor = globalStore().cache.benefitTaxFactorsPerYear[w.weekYear.Year];
      if (!factor) {
        this.missingTaxError =
          "saknar värde för förmånsbeskattning för år " + w.weekYear.Year;
      } else {
        this.missingTaxError = null;
      }

      if (!city) {
        w.tax = 0;
        return;
      }

      if (!w.nights) {
        w.tax = 0;
        return;
      }

      if (!this.form.values.area) {
        w.tax = 0;
        return;
      }

      w.tax = getTax(
        w.weekYear.Year,
        city.taxRegion,
        city.inhabitants,
        toFinite(w.nights),
        toFinite(this.form.values.area)
      );
    });
  };

  get weeksValue() {
    if (!this.form.values.weeks) {
      return [];
    }

    return weeksToDurations(
      this.form.values.weeks.map((x) => weekYearToDate(x.weekYear))
    );
  }

  @computed get taxedWeekValue() {
    if (!this.form.values.weeks) {
      return [];
    }

    return weeksToDurations(
      this.form.values.weeks
        .filter((x) => x.isTaxed)
        .map((x) => weekYearToDate(x.weekYear))
    );
  }

  @computed get offerOptions() {
    if (!this.accommodationData) {
      return [];
    }

    return this.accommodationData.Offers.toLabelValue(
      (x) => x.Name,
      (x) => x._id
    );
  }

  @computed get showTaxPaymentWeekSelector() {
    return this.bookingData.BookingGroup.bookings.length > 1;
  }

  @computed get taxPaymentWeekSelectorDisabled() {
    return this.bookingData.BookingGroup.bookings.length === 1;
  }

  @computed get isConsultantEmployee() {
    return this.bookingData.Consultant?.LegalPersonType === LegalPersonType.Employee;
  }
}

export function weekYearToString(v: WeekYear) {
  return `${v.Week}-${v.Year}`;
}

export function parseWeekYear(v: string) {
  const [week, year] = v.split("-");

  return { week: toFinite(week), year: toFinite(year) };
}

function getTax(
  year: number,
  taxRegion: string,
  inhab: number,
  nights: number,
  meters: number
) {
  const factors = globalStore().cache.benefitTaxFactorsPerYear[year];

  // console.log("getTax", year, factors);
  if (!factors) {
    console.error(`!!!! ******** benefitTaxFactorsPerYear is missing for ${year}`);
  }

  let factor: number;

  if (taxRegion === "Stor-Stockholm") {
    factor = factors?.[0];
  }

  if (taxRegion === "Stor-Göteborg") {
    factor = factors?.[1];
  }

  if (taxRegion === "Stor-Malmö") {
    factor = factors?.[2];
  }

  if (!factor && inhab > 75_000) {
    factor = factors?.[3];
  }

  if (!factor && inhab <= 75000) {
    factor = factors?.[4];
  }

  factor = factor || 0;

  return (factor / 30) * meters * nights;
}

function isValidNumber(str: any) {
  // Trim the string using lodash and check if it's a valid number
  const trimmedStr = trim(str);

  // Check if the trimmed string is a valid number using JavaScript's native functions
  return !isNaN(parseFloat(trimmedStr)) && isFinite(Number(trimmedStr));
}
